import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Pencil, Trash2, ExternalLink, Sparkles, DollarSign, LineChart, GraduationCap, Loader2, Search, Link2, X, MapPin, Building2, Camera, ShieldCheck, Tag, Instagram, MessageCircle, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import {
  listListings, createListing, updateListing, deleteListing,
  matchClients, EMPTY_LISTING,
} from './crmApi';
import { api } from '../../context/AuthContext';
import { DataProvidersFooter, SchoolChipLink } from '../../components/DataAttribution';
import { CampaignDialog, CHANNEL_META } from './CrmSettingsSocialMedia';

// ─────────────────────────────────────────────────── Neighborhood chips
// Compact AVM + median-sold + closest-rated-school chips surfaced on each
// listing card, powered by the backend's RentCast + GreatSchools
// enrichment pipeline (`neighborhood_summary` on the public listing).
//
// Behavior:
//   - If enrichment is still pending → single muted "pulling data…" chip.
//   - If enrichment failed           → single amber "data unavailable" chip
//     (realtor can retry via the Admin backfill endpoint).
//   - If any of AVM / sold / school data is present → render those chips.
//   - If NO data is present at all   → render nothing (first-paint clean).
function _formatMoney(n) {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return null;
  if (n >= 1000) return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
}

function NeighborhoodChips({ summary }) {
  if (!summary) return null;
  const { status, avm_value, avm_low, avm_high, sold_median, top_school } = summary;

  if (status === 'pending') {
    return (
      <div data-testid="neighborhood-chip-pending"
           className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-brand-cream text-brand-green/60">
        <Loader2 className="w-3 h-3 animate-spin" /> Pulling neighborhood data…
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div data-testid="neighborhood-chip-failed"
           className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-900">
        Neighborhood data unavailable
      </div>
    );
  }

  const avmLabel = _formatMoney(avm_value);
  const soldLabel = _formatMoney(sold_median);
  // GreatSchools 2024-12 spec, page 8: "small" ratings must be
  // accompanied by "out of ten" or "out of 10" context text, and
  // labeled as "GreatSchools Rating" (page 6, naming conventions).
  const schoolRating = top_school
    && typeof top_school.rating === 'number'
    && top_school.rating >= 1 && top_school.rating <= 10
    ? `${top_school.rating} out of 10`
    : null;
  const anyChip = avmLabel || soldLabel || (top_school && top_school.name);
  if (!anyChip) return null;

  const rangeTitle = (avm_low && avm_high)
    ? `Estimated value range: $${avm_low.toLocaleString()} – $${avm_high.toLocaleString()}`
    : undefined;
  const schoolTitle = top_school && top_school.name
    ? `${top_school.name}${top_school.grades ? ` · ${top_school.grades}` : ''}${top_school.distance_mi != null ? ` · ${top_school.distance_mi} mi` : ''}`
    : undefined;

  return (
    <div className="flex flex-wrap gap-1" data-testid="neighborhood-chips">
      {avmLabel && (
        <span title={rangeTitle}
              data-testid="chip-avm"
              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-brand-green/10 text-brand-green">
          <DollarSign className="w-3 h-3" /> AVM {avmLabel}
        </span>
      )}
      {soldLabel && (
        <span title={summary.sold_year ? `Median sold price in ZIP as of ${summary.sold_year}` : undefined}
              data-testid="chip-sold"
              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-brand-green/10 text-brand-green">
          <LineChart className="w-3 h-3" /> ZIP median {soldLabel}
        </span>
      )}
      {top_school && top_school.name && (
        <SchoolChipLink
          profileUrl={top_school.profile_url}
          name={top_school.name}
          title={schoolTitle}
        >
          <GraduationCap className="w-3 h-3 shrink-0" />
          <span className="truncate">{top_school.name}</span>
          {schoolRating && (
            <span className="shrink-0 opacity-70">
              · GreatSchools Rating {schoolRating}
            </span>
          )}
        </SchoolChipLink>
      )}
    </div>
  );
}

function ListingForm({ value, onChange }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2">
        <Label htmlFor="l-address">Address</Label>
        <Input id="l-address" data-testid="listing-input-address" value={value.address} onChange={set('address')} />
      </div>
      <div>
        <Label htmlFor="l-price">Price</Label>
        <Input id="l-price" data-testid="listing-input-price" placeholder="$845,000" value={value.price} onChange={set('price')} />
      </div>
      <div>
        <Label htmlFor="l-status">Status</Label>
        <Input id="l-status" data-testid="listing-input-status" placeholder="Active" value={value.status} onChange={set('status')} />
      </div>
      <div>
        <Label htmlFor="l-beds">Beds</Label>
        <Input id="l-beds" data-testid="listing-input-beds" value={value.beds} onChange={set('beds')} />
      </div>
      <div>
        <Label htmlFor="l-baths">Baths</Label>
        <Input id="l-baths" data-testid="listing-input-baths" value={value.baths} onChange={set('baths')} />
      </div>
      <div>
        <Label htmlFor="l-sqft">Sqft</Label>
        <Input id="l-sqft" data-testid="listing-input-sqft" value={value.sqft} onChange={set('sqft')} />
      </div>
      <div>
        <Label htmlFor="l-features">Features (comma-separated)</Label>
        <Input
          id="l-features"
          data-testid="listing-input-features"
          value={(value.features || []).join(', ')}
          onChange={(e) => onChange({ ...value, features: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
        />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="l-image">Image URL</Label>
        <Input id="l-image" data-testid="listing-input-image" placeholder="https://…" value={value.image_url} onChange={set('image_url')} />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="l-source">Source URL (Zillow / Redfin / MLS)</Label>
        <Input id="l-source" data-testid="listing-input-source" placeholder="https://…" value={value.source_url} onChange={set('source_url')} />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="l-desc">Description</Label>
        <Textarea id="l-desc" data-testid="listing-input-description" rows={3} value={value.description} onChange={set('description')} />
      </div>
    </div>
  );
}

function ListingCard({ listing, campaigns = [], onEdit, onDelete, onMatch }) {
  // Collect every keyword bound to this listing across all its
  // campaigns. Each chip renders a tiny channel icon (Instagram /
  // Messenger) so realtors see where the auto-DM fires without
  // opening the editor. De-duped case-insensitively but preserves the
  // display casing we got from the backend.
  const keywordChips = [];
  const seen = new Set();
  for (const c of campaigns) {
    const Icon = CHANNEL_META[c.channel]?.Icon;
    const kws = Array.isArray(c.keywords) && c.keywords.length
      ? c.keywords : (c.keyword ? [c.keyword] : []);
    for (const kw of kws) {
      const key = `${c.channel}:${(kw || '').toUpperCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      keywordChips.push({ kw, Icon, active: c.is_active !== false, id: c.id });
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col" data-testid={`listing-card-${listing.id}`}>
      <div className="aspect-[4/3] bg-brand-cream flex items-center justify-center">
        {listing.image_url ? (
          <img src={listing.image_url} alt={listing.address} className="w-full h-full object-cover"
               onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <div className="text-brand-green/40 text-sm">No image</div>
        )}
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-semibold text-brand-green">{listing.address}</div>
            <div className="text-sm text-brand-green/70">{listing.price}</div>
          </div>
          <span className="text-xs px-2 py-1 rounded-full bg-brand-cream text-brand-green whitespace-nowrap">
            {listing.status || 'Active'}
          </span>
        </div>
        <div className="text-sm text-brand-green/70">
          {listing.beds} bd · {listing.baths} ba · {listing.sqft} sqft
        </div>
        {listing.features?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {listing.features.slice(0, 4).map((f) => (
              <span key={f} className="text-xs px-2 py-0.5 bg-brand-cream text-brand-green rounded">{f}</span>
            ))}
          </div>
        )}
        {keywordChips.length > 0 && (
          <div
            data-testid={`listing-keywords-${listing.id}`}
            className="flex flex-wrap gap-1 pt-1"
          >
            {keywordChips.slice(0, 6).map((chip, i) => (
              <span
                key={`${chip.id}-${chip.kw}-${i}`}
                className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${
                  chip.active
                    ? 'border-brand-gold/40 bg-brand-gold/10 text-brand-green'
                    : 'border-brand-green/20 bg-brand-cream text-brand-green/60 line-through'
                }`}
                title={chip.active ? 'Auto-DM keyword' : 'Keyword paused'}
              >
                {chip.Icon ? <chip.Icon className="w-2.5 h-2.5" aria-hidden="true" /> : <Tag className="w-2.5 h-2.5" aria-hidden="true" />}
                {chip.kw}
              </span>
            ))}
            {keywordChips.length > 6 && (
              <span className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border border-brand-green/20 text-brand-green/60">
                +{keywordChips.length - 6}
              </span>
            )}
          </div>
        )}
        <NeighborhoodChips summary={listing.neighborhood_summary} />
        <div className="mt-auto flex items-center justify-between pt-3">
          {listing.source_url ? (
            <a href={listing.source_url} target="_blank" rel="noreferrer"
               className="text-xs text-brand-green/70 hover:text-brand-green inline-flex items-center gap-1">
              Source <ExternalLink className="w-3 h-3" />
            </a>
          ) : <span />}
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" data-testid={`listing-match-${listing.id}`} onClick={() => onMatch(listing)}>
              <Sparkles className="w-4 h-4" /> Match
            </Button>
            <Button size="sm" variant="ghost" data-testid={`listing-edit-${listing.id}`} onClick={() => onEdit(listing)}>
              <Pencil className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" data-testid={`listing-delete-${listing.id}`} onClick={() => onDelete(listing)}>
              <Trash2 className="w-4 h-4 text-red-600" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MatchDialog({ listing, open, onClose }) {
  const [threshold, setThreshold] = useState(80);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const run = useCallback(async () => {
    if (!listing) return;
    setBusy(true);
    try {
      const data = await matchClients(listing.id, threshold);
      setResult(data);
    } catch (err) {
      toast({ title: 'Match failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }, [listing, threshold, toast]);

  useEffect(() => { if (open && listing) { setResult(null); run(); } }, [open, listing, run]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="listing-match-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Match buyers to {listing?.address}</DialogTitle>
          <DialogDescription>
            Buyer clients ranked against this listing's price, beds, baths, sqft and target areas.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3">
          <Label className="text-sm text-brand-green/70">Threshold</Label>
          <Input type="number" min={0} max={100} value={threshold}
                 data-testid="listing-match-threshold"
                 onChange={(e) => setThreshold(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                 className="w-24" />
          <Button size="sm" onClick={run} disabled={busy} data-testid="listing-match-run">
            {busy ? 'Scoring…' : 'Re-score'}
          </Button>
          {result && <span className="text-sm text-brand-green/70">{result.matched_count} match{result.matched_count === 1 ? '' : 'es'}</span>}
        </div>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-brand-cream">
          {result?.clients?.length ? result.clients.map((c) => (
            <div key={c.id} className="py-2 flex items-center justify-between gap-3" data-testid={`match-row-${c.id}`}>
              <div>
                <div className="text-sm font-semibold text-brand-green">{c.name}</div>
                <div className="text-xs text-brand-green/60">{c.phone_number || c.email || '—'}</div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-semibold ${c.matched ? 'text-brand-green' : 'text-brand-green/40'}`}>
                  {c.match_score}%
                </div>
                {c.matched && <div className="text-[10px] uppercase tracking-wide text-brand-gold">match</div>}
              </div>
            </div>
          )) : <p className="text-sm text-brand-green/60 py-4">No buyer clients to score.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ListingsBody({ loading, listings, campaignsByListing, onCreate, onEdit, onDelete, onMatch }) {
  if (loading) return <p className="text-brand-green/60">Loading listings…</p>;
  if (listings.length === 0) {
    return (
      <div className="bg-white rounded-xl p-8 text-center">
        <p className="text-brand-green/70">No listings yet.</p>
        <Button className="mt-3" onClick={onCreate}>Add your first listing</Button>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {listings.map((l) => (
        <ListingCard key={l.id} listing={l}
                     campaigns={campaignsByListing[l.id] || []}
                     onEdit={onEdit} onDelete={onDelete} onMatch={onMatch} />
      ))}
    </div>
  );
}

// ────────────────────────────────────────── Ingestion bar (Nov 2026)
// Brings the web Listings page to parity with the mobile app's two
// entry paths:
//   • Path A — type an address → Google Places autocomplete →
//     RentCast enrichment → listing created.
//   • Path B — paste a URL or MLS blurb → Claude extracts → optional
//     RentCast enrichment → editable draft → save.
// A smaller "Add manually" button keeps the old hand-entry form around
// for off-market / draft cases.
//
// Google Places session token is minted once per typing session so we
// pay for a single Places session instead of per-keystroke billing.

function _newGoogleSessionToken() {
  // Google only requires a stable opaque string per session. UUID v4
  // in the browser is sufficient and matches what the mobile app sends.
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function ListingIngest({ onImported, onOpenManual, onOpenPaste }) {
  const { toast } = useToast();
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(null);  // placeId being imported
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Fresh session token each time the user starts typing from empty —
  // billing boundary per Places (New) spec.
  useEffect(() => {
    if (!input && sessionTokenRef.current) {
      sessionTokenRef.current = null;
    } else if (input && !sessionTokenRef.current) {
      sessionTokenRef.current = _newGoogleSessionToken();
    }
  }, [input]);

  // 300ms debounce + 3-char minimum — matches the mobile spec exactly.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if ((input || '').trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post('/places/autocomplete', {
          input: input.trim(),
          sessionToken: sessionTokenRef.current,
        });
        setSuggestions(data?.suggestions || []);
        setOpen(true);
      } catch (e) {
        setSuggestions([]);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [input]);

  // Click-outside closes the dropdown.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = async (s) => {
    setPicking(s.placeId);
    setOpen(false);
    try {
      // Resolve the Place to a structured address. The session token is
      // CONSUMED here per the Places (New) billing contract — null it
      // immediately so the next search starts a fresh session.
      const { data: details } = await api.get(
        `/places/details/${encodeURIComponent(s.placeId)}`,
        { params: { sessionToken: sessionTokenRef.current } },
      );
      sessionTokenRef.current = null;
      // Hand off to RentCast for the full enrichment (verified price,
      // beds/baths/sqft, Street View photo, knowledge-base hydration).
      const { data: rc } = await api.post('/listings/rentcast-import', {
        street: details.street, city: details.city,
        state: details.state, zipCode: details.zipCode,
        formattedAddress: details.formattedAddress,
      });
      const draft = rc?.listing || {};
      // Final save → the shared POST /api/listings endpoint.
      const saved = await createListing({
        ...EMPTY_LISTING, ...draft,
        features: Array.isArray(draft.features) ? draft.features : [],
      });
      toast({
        title: 'Listing imported',
        description: `${draft.address || 'Address'} enriched with RentCast + Street View.`,
      });
      setInput('');
      setSuggestions([]);
      onImported(saved);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = err?.response?.status === 404
        ? "RentCast has no record for this address — try 'Add manually'."
        : err?.response?.status === 429
          ? 'RentCast rate-limited us for a minute. Try again shortly.'
          : (typeof detail === 'string' ? detail : null)
            || 'Import failed. Try again or add manually.';
      toast({ title: 'Import failed', description: msg, variant: 'destructive' });
    } finally {
      setPicking(null);
    }
  };

  return (
    <div className="rounded-2xl border border-brand-green/15 bg-white p-4 mb-6"
         data-testid="listing-ingest">
      <div className="flex items-center gap-2">
        <div className="relative flex-1" ref={containerRef}>
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <Search className="w-4 h-4 text-brand-green/50" />
          </div>
          <Input
            data-testid="listing-ingest-search"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={() => suggestions.length && setOpen(true)}
            placeholder="Type an address — autocomplete, verify, import"
            className="pl-9 pr-9"
            disabled={!!picking}
          />
          {input && !picking && (
            <button type="button" onClick={() => setInput('')}
                    data-testid="listing-ingest-clear"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-green/40 hover:text-brand-green">
              <X className="w-4 h-4" />
            </button>
          )}
          {picking && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="w-4 h-4 animate-spin text-brand-green" />
            </div>
          )}
          {open && suggestions.length > 0 && (
            <div data-testid="listing-ingest-suggestions"
                 className="absolute z-20 left-0 right-0 mt-1 rounded-lg border border-brand-green/15 bg-white shadow-lg overflow-hidden">
              {suggestions.slice(0, 6).map((s) => (
                <button key={s.placeId} type="button"
                        data-testid={`listing-ingest-suggestion-${s.placeId}`}
                        onClick={() => pick(s)}
                        className="w-full text-left px-3 py-2 hover:bg-brand-cream/60 border-b border-brand-green/5 last:border-b-0">
                  <div className="text-sm text-brand-green font-medium truncate">
                    {s.mainText || s.description}
                  </div>
                  {s.secondaryText && (
                    <div className="text-[11px] text-brand-green/60 truncate">
                      {s.secondaryText}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button type="button" variant="outline"
                data-testid="listing-ingest-paste"
                onClick={onOpenPaste} disabled={!!picking}>
          <Link2 className="w-4 h-4 mr-1" /> Paste link
        </Button>
        <Button type="button" variant="ghost" size="sm"
                data-testid="listing-ingest-manual"
                onClick={onOpenManual} disabled={!!picking}>
          Add manually
        </Button>
      </div>
      <div
        data-testid="listing-ingest-trust-bar"
        className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-brand-green/60"
      >
        <span className="inline-flex items-center gap-1">
          <ShieldCheck className="w-3.5 h-3.5 text-brand-green/50" aria-hidden="true" />
          <span className="font-medium text-brand-green/70">Enterprise data</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5 text-brand-green/50" aria-hidden="true" />
          Google Places
        </span>
        <span className="inline-flex items-center gap-1">
          <Building2 className="w-3.5 h-3.5 text-brand-green/50" aria-hidden="true" />
          RentCast AVM
        </span>
        <span className="inline-flex items-center gap-1">
          <Camera className="w-3.5 h-3.5 text-brand-green/50" aria-hidden="true" />
          Street View
        </span>
      </div>
      <p className="text-[11px] text-brand-green/55 mt-1">
        Paste a link or MLS blurb and we'll extract the details with Claude.
      </p>
    </div>
  );
}


// Paste-link dialog — Path B. Shows a textarea, calls /listings/extract,
// then opens an editable draft the user can tweak before saving.
function PasteLinkDialog({ open, onClose, onDraft }) {
  const { toast } = useToast();
  const [content, setContent] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setContent(''); setBusy(false); }
  }, [open]);

  const extract = async () => {
    if (!content.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post('/listings/extract',
                                      { content: content.trim() });
      if (data?.note) {
        toast({ title: data.enriched ? 'Enriched with RentCast' : 'Draft ready',
                description: data.note });
      }
      onDraft(data?.listing || {}, data?.enriched);
      onClose();
    } catch (e) {
      toast({
        title: 'Extract failed',
        description: e?.response?.data?.detail || 'Try pasting the listing text instead.',
        variant: 'destructive',
      });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="listing-paste-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Paste a listing link or details</DialogTitle>
          <DialogDescription>
            Zillow, Redfin, Realtor.com, your MLS — paste the URL and/or the listing text. We'll fetch the page, parse the details with Claude, and verify against RentCast if the address is real.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          data-testid="listing-paste-textarea"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="https://www.redfin.com/… or paste the listing text here"
          rows={8}
          maxLength={20000}
          autoFocus
        />
        <div
          data-testid="listing-paste-trust-bar"
          className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-brand-green/60"
        >
          <span className="inline-flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-brand-green/50" aria-hidden="true" />
            <span className="font-medium text-brand-green/70">Enterprise data</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5 text-brand-green/50" aria-hidden="true" />
            Claude extract
          </span>
          <span className="inline-flex items-center gap-1">
            <Building2 className="w-3.5 h-3.5 text-brand-green/50" aria-hidden="true" />
            RentCast AVM
          </span>
          <span className="inline-flex items-center gap-1">
            <Camera className="w-3.5 h-3.5 text-brand-green/50" aria-hidden="true" />
            Street View
          </span>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={extract} disabled={busy || !content.trim()}
                  data-testid="listing-paste-extract-btn">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Extracting…</> : 'Extract'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function CrmListings() {
  const [listings, setListings] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);       // listing object or null
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_LISTING);
  const [busy, setBusy] = useState(false);
  const [matchTarget, setMatchTarget] = useState(null);
  const [showPaste, setShowPaste] = useState(false);
  // Campaign-editor state. When `campaignTargetListingId` is set, the
  // CampaignDialog opens with that listing pre-selected (edit flow kicks
  // in when `editingCampaign` is non-null; create flow otherwise).
  const [campaignTargetListingId, setCampaignTargetListingId] = useState('');
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [showCampaign, setShowCampaign] = useState(false);
  const { toast } = useToast();

  // Fetch campaigns in bulk so the listing cards show their bound
  // keywords and the "Add keyword" button inside the edit dialog can
  // list existing campaigns. 500-row cap mirrors the backend list.
  const refreshCampaigns = useCallback(async () => {
    try {
      const { data } = await api.get('/social-media/campaigns');
      setCampaigns(Array.isArray(data) ? data : []);
    } catch {
      /* non-fatal — chip row just stays empty */
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [rows] = await Promise.all([listListings(), refreshCampaigns()]);
      setListings(rows);
    } finally { setLoading(false); }
  }, [refreshCampaigns]);
  useEffect(() => { refresh(); }, [refresh]);

  // Group campaigns by listing_id so lookups are O(1) during render.
  const campaignsByListing = useMemo(() => {
    const map = {};
    for (const c of campaigns) {
      (map[c.listing_id] = map[c.listing_id] || []).push(c);
    }
    return map;
  }, [campaigns]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_LISTING); setShowForm(true); };
  const openEdit = (l) => { setEditing(l); setForm({ ...EMPTY_LISTING, ...l }); setShowForm(true); };

  // Open the shared CampaignDialog in create mode, pre-bound to the
  // listing currently being edited. Realtors never have to re-pick the
  // listing when they're already inside its editor.
  const openAddKeyword = () => {
    if (!editing?.id) {
      toast({ title: 'Save the listing first',
              description: 'Keywords bind to an existing listing — save new listings before adding keywords.' });
      return;
    }
    setCampaignTargetListingId(editing.id);
    setEditingCampaign(null);
    setShowCampaign(true);
  };

  const openEditKeyword = (campaign) => {
    setCampaignTargetListingId(campaign.listing_id);
    setEditingCampaign(campaign);
    setShowCampaign(true);
  };

  // Optimistic in-place flip so the switch doesn't lag on slow networks.
  // On PATCH failure we roll the UI back to the server-authoritative state
  // via a full `refreshCampaigns()` so we never desync.
  const toggleKeywordActive = async (campaign, nextActive) => {
    setCampaigns((prev) => prev.map((c) =>
      c.id === campaign.id ? { ...c, is_active: nextActive } : c));
    try {
      await api.patch(
        `/listings/${campaign.listing_id}/social-campaign/${campaign.id}/active`,
        { is_active: nextActive },
      );
    } catch (err) {
      toast({
        title: nextActive ? 'Resume failed' : 'Pause failed',
        description: err?.response?.data?.detail || 'Try again.',
        variant: 'destructive',
      });
      await refreshCampaigns();
    }
  };

  const deleteKeyword = async (campaign) => {
    if (!window.confirm(`Delete keyword "${(campaign.keywords || [campaign.keyword]).join(', ')}"?`)) return;
    try {
      await api.delete(`/listings/${campaign.listing_id}/social-campaign/${campaign.id}`);
      toast({ title: 'Keyword removed' });
      await refreshCampaigns();
    } catch (err) {
      toast({ title: 'Delete failed',
              description: err?.response?.data?.detail || 'Try again.',
              variant: 'destructive' });
    }
  };

  // Path A — autocomplete + RentCast — saved directly inside the ingest
  // component (no review step; RentCast's data is trusted). Refresh so
  // the new row appears, and start polling for neighborhood facts.
  const onImported = useCallback(() => { refresh(); }, [refresh]);

  // Path B — paste → extract returns a draft. We open the editor dialog
  // pre-filled so the realtor can review before Save. `features` arrives
  // as an array; the manual form reads it as a comma-joined string.
  const onPasteDraft = useCallback((draft) => {
    const features = Array.isArray(draft.features)
      ? draft.features.join(', ') : (draft.features || '');
    setEditing(null);
    setForm({ ...EMPTY_LISTING, ...draft, features });
    setShowForm(true);
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        await updateListing(editing.id, form);
        toast({ title: 'Listing updated' });
      } else {
        await createListing(form);
        toast({ title: 'Listing created' });
      }
      setShowForm(false);
      await refresh();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.address}"? This can't be undone.`)) return;
    try {
      await deleteListing(l.id);
      toast({ title: 'Listing deleted' });
      await refresh();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  // Keywords already bound to the listing currently in the edit dialog —
  // rendered inside the form so realtors see what's wired up before
  // adding more.
  const editingListingCampaigns = editing?.id
    ? (campaignsByListing[editing.id] || []) : [];

  return (
    <div data-testid="crm-listings">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl text-brand-green">Listings</h2>
      </div>

      <ListingIngest
        onImported={onImported}
        onOpenManual={openCreate}
        onOpenPaste={() => setShowPaste(true)}
      />

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent data-testid="listing-form-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit listing' : 'New listing'}</DialogTitle>
            <DialogDescription>
              Address, price and photo details for this property. Shared with the LISTING AiGENT mobile app.
            </DialogDescription>
          </DialogHeader>
          <ListingForm value={form} onChange={setForm} />

          {editing?.id && (
            <ListingKeywordsSection
              campaigns={editingListingCampaigns}
              onAdd={openAddKeyword}
              onEdit={openEditKeyword}
              onDelete={deleteKeyword}
              onToggleActive={toggleKeywordActive}
            />
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !form.address} data-testid="listing-save-btn">
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CampaignDialog
        open={showCampaign}
        listings={listings}
        editing={editingCampaign}
        initialListingId={campaignTargetListingId}
        onClose={() => setShowCampaign(false)}
        onSaved={async () => {
          setShowCampaign(false);
          await refreshCampaigns();
        }}
      />

      <PasteLinkDialog
        open={showPaste}
        onClose={() => setShowPaste(false)}
        onDraft={onPasteDraft}
      />

      <ListingsBody
        listings={listings}
        campaignsByListing={campaignsByListing}
        loading={loading}
        onCreate={openCreate}
        onEdit={openEdit}
        onDelete={remove}
        onMatch={(x) => setMatchTarget(x)}
      />

      {/* GreatSchools + RentCast attribution — mandated by GreatSchools
          2024-12 attribution policy; also discloses RentCast as the
          AVM / sold-price source for CCPA/GDPR hygiene. The
          "Ratings are on a 1–10 scale" sentence only renders when at
          least one listing's top school came back with a numeric
          rating (higher-tier GS subscription); on the free/basic tier
          it stays off so the footer doesn't imply ratings are shown. */}
      <DataProvidersFooter
        showRatingScale={listings.some((l) => {
          const r = l?.neighborhood_summary?.top_school?.rating;
          return typeof r === 'number' && r >= 1 && r <= 10;
        })}
      />

      <MatchDialog listing={matchTarget} open={!!matchTarget} onClose={() => setMatchTarget(null)} />
    </div>
  );
}


// Compact "fires-ago" relative formatter — "2m ago", "5h ago", "3d ago",
// collapsing anything older than 30 days to "30d+ ago" so the chip stays
// 6-ish characters wide. Returns null for falsy / unparseable input so
// callers can skip rendering.
function fmtFiresAgo(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const diff = Math.max(0, Date.now() - t) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const days = Math.floor(diff / 86400);
  return days > 30 ? '30d+ ago' : `${days}d ago`;
}


// Inline keyword manager rendered inside the listing editor. Shows every
// campaign bound to the current listing + an "Add keyword" CTA that
// opens the shared CampaignDialog pre-bound to this listing.
function ListingKeywordsSection({ campaigns, onAdd, onEdit, onDelete, onToggleActive }) {
  return (
    <div
      data-testid="listing-keywords-section"
      className="rounded-xl border border-brand-green/15 bg-brand-cream/40 p-3 mt-2"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-brand-green/70" />
          <Label className="text-sm text-brand-green font-medium">Social keywords</Label>
          <span className="text-[11px] text-brand-green/60">
            {campaigns.length === 0 ? 'None bound yet' : `${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <Button
          type="button" size="sm" variant="outline"
          data-testid="listing-add-keyword-btn"
          onClick={onAdd}
        >
          <Plus className="w-4 h-4 mr-1" /> Add keyword
        </Button>
      </div>
      {campaigns.length > 0 && (
        <div className="mt-3 divide-y divide-brand-green/10">
          {campaigns.map((c) => {
            const kws = (Array.isArray(c.keywords) && c.keywords.length
              ? c.keywords : (c.keyword ? [c.keyword] : []));
            const Icon = CHANNEL_META[c.channel]?.Icon;
            const triggers = Number(c.trigger_count || 0);
            const firedAgo = fmtFiresAgo(c.last_triggered_at);
            return (
              <div key={c.id}
                   data-testid={`listing-keyword-row-${c.id}`}
                   className="py-2 flex items-center gap-2">
                {Icon ? <Icon className="w-4 h-4 text-brand-green/60" /> : null}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1">
                    {kws.map((kw, i) => (
                      <span key={`${c.id}-${kw}-${i}`}
                            className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full border ${
                              c.is_active
                                ? 'border-brand-gold/40 bg-brand-gold/10 text-brand-green'
                                : 'border-brand-green/20 bg-brand-cream text-brand-green/60 line-through'
                            }`}>
                        {kw}
                      </span>
                    ))}
                    {c.name && (
                      <span className="text-[11px] text-brand-green/50 ml-1">
                        · {c.name}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-brand-green/60">
                    <span
                      className="inline-flex items-center gap-1"
                      data-testid={`listing-keyword-trigger-count-${c.id}`}
                      title="Times this keyword fired an auto-DM"
                    >
                      <Zap className="w-3 h-3 text-brand-gold/80" aria-hidden="true" />
                      {triggers === 0
                        ? 'No fires yet'
                        : `${triggers.toLocaleString()} fire${triggers === 1 ? '' : 's'}`}
                    </span>
                    {firedAgo && (
                      <span
                        data-testid={`listing-keyword-last-fired-${c.id}`}
                        title={c.last_triggered_at}
                      >
                        last · {firedAgo}
                      </span>
                    )}
                  </div>
                </div>
                <Switch
                  checked={!!c.is_active}
                  onCheckedChange={(v) => onToggleActive(c, v)}
                  data-testid={`listing-keyword-toggle-${c.id}`}
                  aria-label={c.is_active ? 'Pause keyword' : 'Resume keyword'}
                />
                <Button size="sm" variant="ghost"
                        data-testid={`listing-keyword-edit-${c.id}`}
                        onClick={() => onEdit(c)}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="ghost"
                        data-testid={`listing-keyword-delete-${c.id}`}
                        onClick={() => onDelete(c)}>
                  <Trash2 className="w-3.5 h-3.5 text-red-600" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
